import type {
  HypothesesBlock,
  MetricItem,
  ReportBlock,
  TableBlock,
} from '#models';
import { normalizeGraphBlock } from './graph-block.mapper';

const BLOCK_TYPES = new Set([
  'paragraph',
  'subheading',
  'metrics',
  'table',
  'graph',
  'hypotheses',
  'list',
  'source',
  'workflow',
]);

export function normalizeReportBlocks(json: string | null | undefined): ReportBlock[] | null {
  if (!json) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }

  const rawBlocks = extractBlocks(parsed);
  if (!rawBlocks) {
    return null;
  }

  const blocks = rawBlocks
    .map(block => normalizeBlock(block))
    .filter((block): block is ReportBlock => block !== null);

  return blocks.length > 0 ? blocks : null;
}

function extractBlocks(parsed: unknown): unknown[] | null {
  if (
    parsed &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed) &&
    Array.isArray((parsed as Record<string, unknown>)['blocks'])
  ) {
    return (parsed as Record<string, unknown>)['blocks'] as unknown[];
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed) &&
    BLOCK_TYPES.has(String((parsed as Record<string, unknown>)['type']))
  ) {
    return [parsed];
  }

  if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object') {
    if (looksLikeBlockArray(parsed)) {
      return parsed as unknown[];
    }

    return [{ type: 'table', rows: parsed }];
  }

  return null;
}

function looksLikeBlockArray(arr: unknown[]): boolean {
  return arr.every(
    el =>
      !!el &&
      typeof el === 'object' &&
      !Array.isArray(el) &&
      BLOCK_TYPES.has(String((el as Record<string, unknown>)['type']))
  );
}

function normalizeBlock(block: unknown): ReportBlock | null {
  if (!block || typeof block !== 'object') {
    return null;
  }

  const data = block as Record<string, unknown>;
  switch (data['type']) {
    case 'paragraph':
      return { type: 'paragraph', html: String(data['html'] ?? data['text'] ?? '') };
    case 'subheading':
      return { type: 'subheading', text: String(data['text'] ?? '') };
    case 'table':
      return normalizeTable(data);
    case 'graph':
      return normalizeGraphBlock(data);
    case 'hypotheses':
      return normalizeHypotheses(data);
    case 'list':
      return {
        type: 'list',
        ordered: Boolean(data['ordered']),
        items: (Array.isArray(data['items']) ? (data['items'] as unknown[]) : []).map(String),
      };
    case 'metrics':
      return { type: 'metrics', items: Array.isArray(data['items']) ? (data['items'] as MetricItem[]) : [] };
    case 'source':
      return { type: 'source', text: String(data['text'] ?? '') };
    default:
      return null;
  }
}

function normalizeTable(data: Record<string, unknown>): TableBlock {
  const rows = Array.isArray(data['rows']) ? (data['rows'] as unknown[]) : [];

  if (Array.isArray(data['headers'])) {
    return {
      type: 'table',
      headers: (data['headers'] as unknown[]).map(String),
      rows: rows.map(row => (Array.isArray(row) ? (row as unknown[]).map(String) : [])),
    };
  }

  if (rows.length > 0 && typeof rows[0] === 'object' && !Array.isArray(rows[0])) {
    const objectRows = rows as Record<string, unknown>[];
    const headers: string[] = [];
    for (const row of objectRows) {
      for (const key of Object.keys(row)) {
        if (!headers.includes(key)) {
          headers.push(key);
        }
      }
    }

    return {
      type: 'table',
      headers,
      rows: objectRows.map(row => headers.map(header => String(row[header] ?? ''))),
    };
  }

  return {
    type: 'table',
    headers: [],
    rows: rows.map(row => (Array.isArray(row) ? (row as unknown[]).map(String) : [])),
  };
}

function normalizeHypotheses(data: Record<string, unknown>): HypothesesBlock {
  const items = Array.isArray(data['items']) ? (data['items'] as Record<string, unknown>[]) : [];
  return {
    type: 'hypotheses',
    items: items.map(item => ({
      rank: Number(item['rank']) || 0,
      tag: String(item['tag'] ?? ''),
      title: String(item['title'] ?? item['hypothesis'] ?? ''),
      body: String(item['body'] ?? item['support'] ?? ''),
    })),
  };
}
